import React from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PropertyCard = ({ property, onCheckout }) => {
  const navigate = useNavigate();

  const handleViewDetails = () => {
    navigate(`/real-estate-portal/property/${property?.id}`);
  };

  const handleWhatsAppInquiry = () => {
    const message = encodeURIComponent(`Hello, I'm interested in ${property?.title} (${property?.id})`);
    window.open(`https://wa.me/243818883531?text=${message}`, '_blank');
  };

  const handleCheckout = () => {
    onCheckout?.(property);
  };

  return (
    <div className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-2xl hover:border-blue-500/30 transition-all duration-300 group">
      <div className="relative h-48 md:h-56 lg:h-64 overflow-hidden">
        <Image
          src={property?.image}
          alt={property?.imageAlt}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${property?.status === 'available' ? 'bg-green-500 text-white'
              : property?.status === 'rented' ? 'bg-yellow-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
            }`}>
            {property?.status === 'available' ? 'Disponible' : property?.status === 'rented' ? 'Loué' : 'Réservé'}
          </span>
          {property?.featured && (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-500 text-white">
              Vedette
            </span>
          )}
        </div>
        <button
          onClick={(e) => {
            e?.stopPropagation();
            property?.onToggleFavorite?.(property?.id);
          }}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-slate-900/80 backdrop-blur-sm flex items-center justify-center hover:bg-slate-900 transition-colors"
          aria-label="Add to favorites"
        >
          <Icon
            name={property?.isFavorite ? 'Heart' : 'Heart'}
            size={20}
            className={property?.isFavorite ? 'text-red-400 fill-current' : 'text-white'}
          />
        </button>
      </div>
      <div className="p-4 md:p-5 lg:p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg md:text-xl font-semibold text-slate-900 dark:text-white mb-1 line-clamp-1">
              {property?.title}
            </h3>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <Icon name="MapPin" size={16} />
              <span className="line-clamp-1">{property?.location}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 mb-4 text-sm text-slate-600 dark:text-slate-400">
          <div className="flex items-center gap-1">
            <Icon name="Bed" size={18} />
            <span>{property?.bedrooms}</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Bath" size={18} />
            <span>{property?.bathrooms}</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Maximize" size={18} />
            <span className="whitespace-nowrap">{property?.area} m²</span>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200 dark:border-white/10">
          <div>
            <p className="text-2xl md:text-3xl font-bold text-blue-400">
              {property?.price?.toLocaleString('fr-FR')} CDF
            </p>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
              ${property?.priceUSD?.toLocaleString('en-US')} USD
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-slate-900 dark:text-white">{property?.type}</p>
            <p className="text-xs text-slate-600 dark:text-slate-400">{property?.category}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="MessageCircle"
            iconPosition="left"
            onClick={handleWhatsAppInquiry}
            className="flex-1"
          >
            WhatsApp
          </Button>
          {property?.category === 'Location' && property?.status === 'available' ? (
            <Button
              variant="default"
              size="sm"
              iconName="CreditCard"
              iconPosition="left"
              onClick={handleCheckout}
              className="flex-1"
            >
              Louer
            </Button>
          ) : (
            <Button
              variant="default"
              size="sm"
              iconName="Eye"
              iconPosition="left"
              onClick={handleViewDetails}
              className="flex-1"
            >
              Détails
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;
